import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';
import DashboardCard08 from '../partials/dashboard/DashboardCard03';
import BeatLoader from 'react-spinners/BeatLoader';
import { ArrowUpIcon } from '@heroicons/react/24/solid';
import { motion, AnimatePresence } from 'framer-motion';
import { tailwindConfig } from '../utils/Utils';

function Tooltip({ children, text }) {
  return (
    <div className="group relative inline-block">
      {children}
      <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 -top-9 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
        <div className="bg-gray-900 text-white text-sm px-2 py-1 rounded whitespace-nowrap" style={{fontFamily: '"Share Tech Mono", monospace'}}>
          {text}
          <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-gray-900"></div>
        </div>
      </div>
    </div>
  );
}

function StatsSkeleton() {
  return (
    <>
      <div className="rounded-xl overflow-hidden">
        <div className="grid grid-cols-3 sm:grid-cols-2 lg:grid-cols-3 gap-2 pt-5">
          {[0, 1, 2].map((index) => (
            <div 
              key={index}
              className="shadow-sm rounded-xl p-4 sm:p-4 transition-all duration-300"
              style={{
                background: 'linear-gradient(135deg, #2C3E50 0%, #34495E 100%)',
                border: '2px solid rgba(241, 196, 15, 0.3)',
                fontFamily: '"Share Tech Mono", monospace'
              }}
            >
              {/* Title skeleton */}
              <div className="h-4 rounded w-2/3 mb-3 animate-pulse" style={{background: 'rgba(241, 196, 15, 0.3)'}} />
              
              {/* Value skeleton */}
              <div className="flex items-center">
                <div className="h-8 rounded w-1/2 animate-pulse" style={{background: 'rgba(241, 196, 15, 0.2)'}} />
                <div className="h-6 rounded-full w-12 ml-2 animate-pulse" style={{background: 'rgba(241, 196, 15, 0.1)'}} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Chart Skeleton */}
      <div className="mt-6 shadow-sm rounded-xl p-4" style={{
        background: 'linear-gradient(135deg, #2C3E50 0%, #34495E 100%)',
        border: '2px solid rgba(241, 196, 15, 0.3)'
      }}>
        <div className="h-[300px] relative">
          {/* Y-axis line */}
          <div className="absolute left-0 top-0 bottom-0 w-px" style={{background: 'rgba(241, 196, 15, 0.3)'}} />
          
          {/* X-axis line */}
          <div className="absolute left-0 bottom-0 right-0 h-px" style={{background: 'rgba(241, 196, 15, 0.3)'}} />
          
          {/* Fake data points */}
          <div className="absolute inset-0 flex items-end justify-between px-4">
            {[...Array(7)].map((_, i) => (
              <motion.div
                key={i}
                className="w-1 rounded-t animate-pulse"
                style={{background: 'rgba(241, 196, 15, 0.4)'}}
                initial={{ height: "20%" }}
                animate={{ height: `${20 + Math.random() * 50}%` }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  repeatType: "reverse",
                  ease: "easeInOut"
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

function EmptyState({ onSetLive }) {
  return (
    <div className="mt-4">
      {/* Preview Grid */}
      <div className="grid grid-cols-3 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {[
          { title: 'Total Plays', description: 'Number of game plays', icon: '🎮' },
          { title: 'Shares', description: 'Times shared by players', icon: '🚀' },
          { title: 'Call to Actions', description: 'Call to action click count', icon: '🎯' },
        ].map((metric, index) => (
          <div 
            key={index}
            className="shadow-sm rounded-xl p-4 sm:p-4 transition-all duration-300 hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, #2C3E50 0%, #34495E 100%)',
              border: '2px solid rgba(241, 196, 15, 0.3)',
              boxShadow: '0 0 20px rgba(52, 152, 219, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
              fontFamily: '"Share Tech Mono", monospace'
            }}
          >
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-bold" style={{color: '#F1C40F'}}>
                {metric.title}
              </h4>
              <div className="h-7 w-7 rounded-full flex items-center justify-center" style={{
                background: 'rgba(241, 196, 15, 0.2)',
                border: '1px solid rgba(241, 196, 15, 0.4)'
              }}>
                <span className="text-sm">{metric.icon}</span>
              </div>
            </div>
            <p className="text-xs" style={{color: '#95A5A6'}}>
              {metric.description}
            </p>
          </div>
        ))}
      </div>

      {/* Chart Preview */}
      <div className="mt-6">
        <div className="shadow-sm rounded-xl p-4" style={{
          background: 'linear-gradient(135deg, #2C3E50 0%, #34495E 100%)',
          border: '2px solid rgba(241, 196, 15, 0.3)'
        }}>
          <div className="h-[300px] relative">
            {/* Grid lines */}
            <div className="absolute inset-0 grid grid-cols-6 gap-x-4">
              {[...Array(7)].map((_, i) => (
                <div key={i} className="h-full" style={{borderLeft: '1px solid rgba(241, 196, 15, 0.1)'}} />
              ))}
            </div>
            <div className="absolute inset-0 grid grid-rows-4 gap-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="w-full" style={{borderBottom: '1px solid rgba(241, 196, 15, 0.1)'}} />
              ))}
            </div>

            {/* Example data points */}
            <div className="absolute inset-x-0 bottom-8 h-3/4">
              <svg className="w-full h-full" preserveAspectRatio="none">
                <path
                  d="M 0 160 C 100 120, 200 180, 300 60"
                  fill="none"
                  strokeWidth="2"
                  strokeLinecap="round"
                  style={{stroke: 'rgba(241, 196, 15, 0.4)'}}
                />
              </svg>
            </div>

            {/* Overlay with button */}
            <div className="absolute inset-0 flex items-center justify-center" style={{background: 'rgba(44, 62, 80, 0.8)'}}>
              <div className="text-center space-y-3">
                <button
                  onClick={() => onSetLive()}
                  className="inline-flex items-center px-6 py-3 text-sm font-bold rounded-lg transition-all duration-300 hover:scale-105"
                  style={{
                    background: 'linear-gradient(135deg, #E74C3C 0%, #C0392B 100%)',
                    border: '2px solid #F1C40F',
                    color: '#F1C40F',
                    fontFamily: '"Share Tech Mono", monospace'
                  }}
                >
                  <span className="mr-2">▶</span>
                  ACTIVATE CAMPAIGN
                </button>
                <p className="text-xs font-bold" style={{color: '#95A5A6', fontFamily: '"Share Tech Mono", monospace'}}>
                  &gt;&gt;&gt; STATS WILL DISPLAY WHEN CAMPAIGN IS LIVE &lt;&lt;&lt;
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ChartContent({ chartData, chartView, onChartViewChange, isChartSwitching, isUsingCache }) {
  return (
    <div className="mt-6 relative">
      {/* Loading overlay during transitions */}
      {isChartSwitching && (
        <div className="absolute inset-0 flex items-center justify-center z-10 rounded-xl" style={{
          background: 'rgba(44, 62, 80, 0.9)',
          backdropFilter: 'blur(4px)'
        }}>
          <div className="flex items-center space-x-3 p-4 rounded-lg" style={{
            background: 'linear-gradient(135deg, #2C3E50, #34495E)',
            border: '2px solid rgba(241, 196, 15, 0.4)',
            fontFamily: '"Share Tech Mono", monospace'
          }}>
            <div className="animate-spin w-5 h-5" style={{color: '#F1C40F'}}>
              ⚙️
            </div>
            <span className="text-sm font-bold" style={{color: '#F1C40F'}}>
              SWITCHING VIEW...
            </span>
          </div>
        </div>
      )}
      
      <DashboardCard08 
        chartData={chartData} 
        chartView={chartView}
        onChartViewChange={onChartViewChange}
        isUsingCache={isUsingCache}
      />
    </div>
  );
}

// Add caching constants
const STATS_CACHE_KEY = 'dashboard_stats_cache_v2'; // v2 for new data format
const CHART_CACHE_KEY = 'dashboard_chart_cache_v2'; // v2 for new data format
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Helper functions for caching
const getCachedData = (key) => {
  try {
    const cached = localStorage.getItem(key);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Date.now() - parsed.timestamp < CACHE_DURATION) {
        return parsed.data;
      }
    }
  } catch (error) {
    console.error('Error reading cache:', error);
  }
  return null;
};

const setCachedData = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
  } catch (error) {
    console.error('Error setting cache:', error);
  }
};

export { StatsSkeleton };

export default function Stats({ campaignIds, campaign, onUpdateStatus }) {
  const [stats, setStats] = useState([]);
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isChartLoading, setIsChartLoading] = useState(true);
  const [isChartUpdating, setIsChartUpdating] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [chartView, setChartView] = useState('today');
  const [isChartSwitching, setIsChartSwitching] = useState(false);
  const [isUsingCache, setIsUsingCache] = useState(false);
  const chartTimeoutRef = useRef(null);
  const hasFetchedData = useRef(false);

  const normalizeDate = (date) => new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));

  const getCacheKey = (campaignIds, view) => `${STATS_CACHE_KEY}_${campaignIds.join('_')}_${view}`;
  
  const getCachedStats = (cacheKey) => getCachedData(cacheKey);
  const setCachedStats = (cacheKey, stats, chartData) => setCachedData(cacheKey, { stats, chartData });

  // Handler for chart view changes with loading state
  const handleChartViewChange = async (newView) => {
    if (newView === chartView) return;
    
    setIsChartSwitching(true);
    
    // Small delay to show loading state
    await new Promise(resolve => setTimeout(resolve, 100));
    
    setChartView(newView);
  };

  // OPTIMIZED: Sample-based statistics instead of full aggregation
  const fetchSampleBasedStats = async () => {
    const today = normalizeDate(new Date());
    const todayStart = new Date(today);
    todayStart.setHours(0, 0, 0, 0);
    
    let totalPlayCountOverall = 0;
    let totalPlayCountToday = 0;
    let totalSharedOverall = 0;
    let totalSharedToday = 0;
    let totalCtaOverall = 0;
    let totalCtaToday = 0;

    // COST OPTIMIZATION: Instead of scanning all docs, sample recent ones and estimate
    for (const gameId of campaignIds) {
      const campaignEventRef = collection(db, 'campaignEvent');
      
      // Sample recent 100 entries to estimate totals (much cheaper than full scan)
      const recentQuery = query(
        campaignEventRef,
        where('game_id', '==', gameId),
        orderBy('timestamp', 'desc'),
        limit(100) // Only read 100 docs max per game
      );
      
      const recentSnapshot = await getDocs(recentQuery);
      const recentEntries = recentSnapshot.docs.map(doc => doc.data());
      
      // Even if no entries, continue to process other campaigns
      if (recentEntries.length === 0) {
        console.log(`📊 Campaign ${gameId} has no activity yet`);
        continue;
      }
      
      // Count today's entries from sample
      const todayEntries = recentEntries.filter(entry => {
        const entryDate = entry.timestamp?.toDate() || new Date(entry.timestamp);
        return entryDate >= todayStart;
      });
      
      const todayShares = todayEntries.filter(entry => entry.shared === true).length;
      const todayCtas = todayEntries.filter(entry => entry.cta === true).length;
      
      // For overall stats, if we got a full 100 recent entries, estimate total
      // This is much cheaper than scanning everything
      if (recentEntries.length === 100) {
        // Estimate multiplier based on oldest entry in sample
        const oldestEntry = recentEntries[recentEntries.length - 1];
        const oldestDate = oldestEntry.timestamp?.toDate() || new Date(oldestEntry.timestamp);
        const daysInSample = Math.max(1, (Date.now() - oldestDate.getTime()) / (1000 * 60 * 60 * 24));
        
        // Conservative estimate: assume linear growth
        const estimateMultiplier = Math.min(10, Math.max(1, 30 / daysInSample)); // Cap at 10x
        
        totalPlayCountOverall += Math.round(recentEntries.length * estimateMultiplier);
        totalSharedOverall += Math.round(recentEntries.filter(e => e.shared).length * estimateMultiplier);
        totalCtaOverall += Math.round(recentEntries.filter(e => e.cta).length * estimateMultiplier);
      } else {
        // If less than 100 entries, use actual count
        totalPlayCountOverall += recentEntries.length;
        totalSharedOverall += recentEntries.filter(e => e.shared).length;
        totalCtaOverall += recentEntries.filter(e => e.cta).length;
      }
      
      // Add today's actual counts
      totalPlayCountToday += todayEntries.length;
      totalSharedToday += todayShares;
      totalCtaToday += todayCtas;
    }

    // ALWAYS return stats, even if all zeros - this shows the user that analytics are working
    
    return [
      { 
        name: 'Total Play Count', 
        shortName: 'Plays', 
        overall: totalPlayCountOverall, 
        today: campaign.status !== 'complete' ? totalPlayCountToday : 0, 
        change: campaign.status !== 'complete' ? totalPlayCountToday : 0 
      },
      { 
        name: 'Total Shares', 
        shortName: 'Shares', 
        overall: totalSharedOverall, 
        today: campaign.status !== 'complete' ? totalSharedToday : 0, 
        change: campaign.status !== 'complete' ? totalSharedToday : 0 
      },
      { 
        name: 'Call to Action Clicks', 
        shortName: 'CTA', 
        overall: totalCtaOverall, 
        today: campaign.status !== 'complete' ? totalCtaToday : 0, 
        change: campaign.status !== 'complete' ? totalCtaToday : 0 
      },
    ];
  };

  // OPTIMIZED: Sample-based chart data
  const fetchTodayHourlyData = async () => {
    const today = normalizeDate(new Date());
    const startOfDay = new Date(today);
    startOfDay.setHours(0, 0, 0, 0);
    
    // Group hours into 4-hour blocks to reduce queries
    const timeBlocks = [
      { name: 'Early Morning', start: 0, end: 6, hours: [0, 1, 2, 3, 4, 5] },
      { name: 'Morning', start: 6, end: 12, hours: [6, 7, 8, 9, 10, 11] },
      { name: 'Afternoon', start: 12, end: 18, hours: [12, 13, 14, 15, 16, 17] },
      { name: 'Evening', start: 18, end: 24, hours: [18, 19, 20, 21, 22, 23] }
    ];
    
    const chartData = [];
    const chartLabels = [];
    
    // COST OPTIMIZATION: Sample recent entries instead of scanning by hour
        for (const gameId of campaignIds) {
          const campaignEventRef = collection(db, 'campaignEvent');
      
      // Get today's entries (limited sample)
      const todayQuery = query(
            campaignEventRef,
            where('game_id', '==', gameId),
        where('timestamp', '>=', startOfDay),
        orderBy('timestamp', 'desc'),
        limit(50) // Limit to 50 entries per game for cost control
          );
          
      const snapshot = await getDocs(todayQuery);
      const todayEntries = snapshot.docs.map(doc => doc.data());
      
      // Distribute entries across time blocks
      for (let i = 0; i < timeBlocks.length; i++) {
        if (chartData.length <= i) {
          chartData.push(0);
          chartLabels.push(timeBlocks[i].name);
        }
        
        const blockStart = timeBlocks[i].start;
        const blockEnd = timeBlocks[i].end;
        
        const blockEntries = todayEntries.filter(entry => {
          const entryDate = entry.timestamp?.toDate() || new Date(entry.timestamp);
          const hour = entryDate.getHours();
          return hour >= blockStart && hour < blockEnd;
        });
        
        chartData[i] += blockEntries.length;
      }
    }
    
    return { chartData, chartLabels, userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone };
  };

  // OPTIMIZED: Sample-based 7-day data
  const fetch7DayData = async () => {
    const today = normalizeDate(new Date());
    const chartData = [];
    const chartLabels = [];
    
    // COST OPTIMIZATION: Sample recent entries and distribute across days
      for (const gameId of campaignIds) {
        const campaignEventRef = collection(db, 'campaignEvent');
      
      // Get recent entries (last 7 days sample)
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      
      const recentQuery = query(
          campaignEventRef,
          where('game_id', '==', gameId),
        where('timestamp', '>=', weekAgo),
        orderBy('timestamp', 'desc'),
        limit(200) // Limit for cost control
        );
        
      const snapshot = await getDocs(recentQuery);
      const recentEntries = snapshot.docs.map(doc => doc.data());
      
      // Distribute across 7 days
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const dateStart = new Date(date);
        dateStart.setHours(0, 0, 0, 0);
        const dateEnd = new Date(date);
        dateEnd.setHours(23, 59, 59, 999);
        
        if (chartData.length <= (6 - i)) {
          chartData.push(0);
      chartLabels.push(date.toISOString().split('T')[0]);
    }
    
        const dayEntries = recentEntries.filter(entry => {
          const entryDate = entry.timestamp?.toDate() || new Date(entry.timestamp);
          return entryDate >= dateStart && entryDate <= dateEnd;
        });
        
        chartData[6 - i] += dayEntries.length;
      }
    }
    
    return { chartData, chartLabels, userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone };
  };

  useEffect(() => {
    async function fetchStatsWithSampling() {
      try {
        setIsLoading(true);
        
        // Check cache first
        const cacheKey = getCacheKey(campaignIds, chartView);
        const cachedData = getCachedStats(cacheKey);
        
        if (cachedData) {
          setStats(cachedData.stats);
          setChartData(cachedData.chartData);
          hasFetchedData.current = true;
        setIsLoading(false);
        setIsChartLoading(false);
          setIsChartSwitching(false);
          setIsUsingCache(true);
        return;
      }

        setIsUsingCache(false);
        
        // Fetch sample-based stats (much cheaper)
        const statComparison = await fetchSampleBasedStats();
        setStats(statComparison);
        
        // Fetch chart data based on current view
        const { chartData, chartLabels, userTimezone } = chartView === 'today' 
          ? await fetchTodayHourlyData() 
          : await fetch7DayData();
        
        const formattedChartData = {
          labels: chartLabels,
          datasets: [{
            label: 'Plays',
            data: chartData,
            borderColor: '#F1C40F', // Retro yellow
            backgroundColor: 'rgba(241, 196, 15, 0.2)', // Subtle yellow fill
            fill: true,
            borderWidth: 3,
            tension: 0.2,
            pointRadius: 0,
            pointHoverRadius: 6,
            pointBackgroundColor: '#F1C40F',
            pointHoverBackgroundColor: '#E74C3C', // Red highlight on hover
            pointBorderWidth: 0,
            pointHoverBorderWidth: 3,
            pointHoverBorderColor: '#F1C40F',
            clip: 20,
          }],
          userTimezone
        };
        
        setChartData(formattedChartData);

        // Cache the results
        setCachedStats(cacheKey, statComparison, formattedChartData);

        hasFetchedData.current = true;
      } catch (error) {
        console.error('Error fetching stats:', error);
        // Set empty chart data on error
        setChartData({
          labels: [],
          datasets: []
        });
        setStats([]);
      } finally {
        setIsLoading(false);
        setIsChartLoading(false);
        setIsChartSwitching(false);
      }
    }

    if (campaignIds && campaignIds.length > 0) {
      fetchStatsWithSampling();
    } else {
      // Set empty data when no campaigns
      setChartData({
        labels: [],
        datasets: []
      });
      setStats([]);
      setIsLoading(false);
      setIsChartLoading(false);
    }
  }, [campaignIds, chartView, campaign.status]);

  const renderComparisonBadge = (change, statName) => {
    if (change > 0) {
      return (
        <Tooltip text={`${change.toLocaleString()} ${statName} today`}>
          <div className="ml-2 text-xs font-bold px-2 py-1 rounded-full flex items-center transition-all duration-300 hover:scale-110" style={{
            background: 'linear-gradient(135deg, #27AE60, #2ECC71)',
            border: '1px solid #F1C40F',
            color: '#FFFFFF',
            textShadow: '1px 1px 0px #1E8449',
            fontFamily: '"Share Tech Mono", monospace',
            boxShadow: '0 0 10px rgba(39, 174, 96, 0.3)'
          }}>
            <span className="mr-1">🔥</span>
            {Math.abs(change).toLocaleString()}
          </div>
        </Tooltip>
      );
    } else {
      return (
        <Tooltip text={`0 ${statName} today`}>
          <div className="ml-2 text-xs font-bold px-2 py-1 rounded-full" style={{
            background: 'rgba(52, 73, 94, 0.6)',
            border: '1px solid rgba(149, 165, 166, 0.3)',
            color: '#95A5A6',
            fontFamily: '"Share Tech Mono", monospace'
          }}>
            -
          </div>
        </Tooltip>
      );
    }
  };

  if ((isLoading || isChartLoading) && !hasFetchedData.current) {
    return <StatsSkeleton />;
  }

  if (campaign.status === 'draft' || campaign.status === 'ready') {
    return (
      <AnimatePresence mode="wait">
        <motion.div
          key={campaign.status}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          <EmptyState onSetLive={() => onUpdateStatus('live')} />
        </motion.div>
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={campaign.status}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
      >
        <div className="rounded-xl overflow-hidden">
          <div className="grid grid-cols-3 sm:grid-cols-2 lg:grid-cols-3 gap-2 pt-5">
            {stats.map((stat, index) => (
              <motion.div 
                className="shadow-sm rounded-xl p-4 sm:p-4 transition-all duration-300 hover:scale-105" 
                key={stat.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                style={{
                  background: 'linear-gradient(135deg, #2C3E50 0%, #34495E 100%)',
                  border: '2px solid rgba(241, 196, 15, 0.3)',
                  boxShadow: '0 0 20px rgba(52, 152, 219, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                  fontFamily: '"Share Tech Mono", monospace'
                }}
              >
                <p className="text-xs sm:text-sm font-bold uppercase text-center sm:text-left" style={{color: '#F1C40F'}}>
                  <span className="block sm:hidden">{stat.shortName}</span>
                  <span className="hidden sm:block">{stat.name}</span>
                </p>
                <div className="flex justify-center sm:justify-start items-center mt-1 sm:mt-2">
                  <p className="text-xl sm:text-3xl font-black leading-none" style={{color: '#ECF0F1'}}>
                    {stat.overall.toLocaleString()}
                  </p>
                  {renderComparisonBadge(stat.change, stat.shortName)}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          {campaign.status === 'draft' || campaign.status === 'ready' ? (
            <EmptyState onSetLive={() => onUpdateStatus('live')} />
          ) : isChartUpdating || isTransitioning ? (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="w-full h-56 shadow-sm rounded-xl p-4 flex justify-center items-center mt-6"
              style={{
                background: 'linear-gradient(135deg, #2C3E50 0%, #34495E 100%)',
                border: '2px solid rgba(241, 196, 15, 0.3)',
                boxShadow: '0 0 20px rgba(52, 152, 219, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
              }}
            >
              <div className="text-center py-8 px-6" style={{
                borderRadius: '8px'
              }}>
                <h3 className="text-sm font-black mb-2 tracking-wider" style={{color: '#F1C40F'}}>
                  CAMPAIGN IS LIVE!
                </h3>
                <p className="text-xs font-bold mb-4" style={{color: '#ECF0F1'}}>
                  Your campaign is collecting data. Charts will appear once users start playing!
                </p>
                <div className="flex items-center justify-center gap-2 text-xs font-bold" style={{color: '#95A5A6'}}>
                  <span className="animate-pulse">●</span>
                  <span>WAITING FOR ACTIVITY</span>
                  <span className="animate-pulse">●</span>
                </div>
              </div>
            </motion.div>
          ) : (() => {
            // Debug the render condition
            const hasChartData = chartData && chartData.datasets && chartData.datasets.length > 0 && chartData.datasets[0].data && chartData.datasets[0].data.some(val => val > 0);
            
            return hasChartData ? (
              <ChartContent 
                chartData={chartData} 
                chartView={chartView}
                onChartViewChange={(newView) => handleChartViewChange(newView)}
                isChartSwitching={isChartSwitching}
                isUsingCache={isUsingCache}
              />
            ) : (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="w-full h-56 shadow-sm rounded-xl p-4 flex justify-center items-center mt-6"
                style={{
                  background: 'linear-gradient(135deg, #2C3E50 0%, #34495E 100%)',
                  border: '2px solid rgba(241, 196, 15, 0.3)',
                  boxShadow: '0 0 20px rgba(52, 152, 219, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                }}
              >
                <div className="text-center py-8 px-6" style={{
                  borderRadius: '8px'
                }}>
                  <h3 className="text-sm font-black mb-2 tracking-wider" style={{color: '#F1C40F'}}>
                    CAMPAIGN IS LIVE!
                  </h3>
                  <p className="text-xs font-bold mb-4" style={{color: '#ECF0F1'}}>
                    Your campaign is collecting data. Charts will appear once users start playing!
                  </p>
                  <div className="flex items-center justify-center gap-2 text-xs font-bold" style={{color: '#95A5A6'}}>
                    <span className="animate-pulse">●</span>
                    <span>WAITING FOR ACTIVITY</span>
                    <span className="animate-pulse">●</span>
                  </div>
                </div>
              </motion.div>
            );
          })()}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
}
